import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { FournisseurAuth, useAuth } from './hooks/useAuth';
import { AppLayout } from './components/AppLayout';
import { Chargement } from './components/Elements';
import { ConnexionPage } from './pages/ConnexionPage';
import { MotDePasseImposePage } from './pages/MotDePasseImposePage';
import { TableauDeBordPage } from './pages/TableauDeBordPage';
import { ActivitesPage } from './pages/ActivitesPage';
import { TypesActivitePage } from './pages/TypesActivitePage';
import { TypesReductionPage } from './pages/TypesReductionPage';
import { EtablissementsPage } from './pages/EtablissementsPage';
import { LieuxPage, PersonnelPage } from './pages/ReferentielsPages';
import { ContratsPage } from './pages/ContratsPage';
import { PaiePage } from './pages/PaiePage';
import { AnneesPage } from './pages/AnneesPage';
import { ModeOperatoireAnneePage } from './pages/ModeOperatoireAnneePage';
import { AdherentsPage } from './pages/AdherentsPage';
import { AdherentDetailPage } from './pages/AdherentDetailPage';
import { PersonnelDetailPage } from './pages/PersonnelDetailPage';
import { AnomaliesPage } from './pages/AnomaliesPage';
import { DemandesPage } from './pages/DemandesPage';
import { InscriptionEnLignePage } from './pages/InscriptionEnLignePage';
import { InscriptionsPage } from './pages/InscriptionsPage';
import { PlanningPage } from './pages/PlanningPage';
import { CreneauxPage } from './pages/CreneauxPage';
import { PresencesPage } from './pages/PresencesPage';
import { ListePresencePage } from './pages/ListePresencePage';
import { PaiementsPage } from './pages/PaiementsPage';
import { CalendrierPage } from './pages/CalendrierPage';
import { EntitesPage } from './pages/admin/EntitesPage';
import { UtilisateursPage } from './pages/admin/UtilisateursPage';
import { RolesPage } from './pages/admin/RolesPage';
import { ParametresPage } from './pages/admin/ParametresPage';
import { AuditPage } from './pages/admin/AuditPage';
import { NouveautesPage } from './pages/NouveautesPage';

/**
 * Garde de route. Le masquage cote client n'est qu'un confort : le
 * backend refuse de toute facon ce que l'utilisateur n'a pas le droit de
 * faire, et c'est lui qui fait foi (RG-35).
 */
function Protegee({
  children,
  permissions,
}: {
  children: React.ReactNode;
  permissions?: string[];
}) {
  const { session, chargement, peut } = useAuth();

  if (chargement) return <Chargement />;
  if (!session) return <Navigate to="/connexion" replace />;
  if (permissions && permissions.length > 0 && !peut(...permissions)) {
    return (
      <div className="carte p-8">
        <h1 className="text-lg font-semibold">Acces refuse</h1>
        <p className="mt-2 text-sm text-encre-500">
          Votre role sur cette entite ne donne pas acces a cet ecran. Changez d’entite active, ou
          demandez une habilitation.
        </p>
      </div>
    );
  }

  return <>{children}</>;
}

function Routage() {
  const { session, chargement } = useAuth();

  if (chargement) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Chargement libelle="Ouverture de la session…" />
      </div>
    );
  }

  // Mot de passe impose : l'ecran de changement remplace TOUT le routage,
  // aucune route n'est atteignable — et le serveur refuse de toute facon
  // le reste (403 changement_mdp_requis).
  if (session?.utilisateur.doitChangerMdp) {
    return <MotDePasseImposePage />;
  }

  return (
    <Routes>
      {/* La seule page PUBLIQUE : le formulaire d'inscription des adultes,
          sans session ni bandeau. Elle ne renvoie pas vers la connexion
          et ne s'en va pas quand on est connecte. */}
      <Route path="/inscription/:code" element={<InscriptionEnLignePage />} />
      <Route path="/inscription" element={<Navigate to="/inscription/ARTISTIC" replace />} />

      <Route
        path="/connexion"
        element={session ? <Navigate to="/" replace /> : <ConnexionPage />}
      />

      <Route
        element={
          <Protegee>
            <AppLayout />
          </Protegee>
        }
      >
        <Route path="/" element={<TableauDeBordPage />} />

        <Route
          path="/anomalies"
          element={
            <Protegee permissions={['personne.lire', 'tableau_bord.lire']}>
              <AnomaliesPage />
            </Protegee>
          }
        />

        <Route
          path="/adherents"
          element={
            <Protegee permissions={['personne.lire']}>
              <AdherentsPage />
            </Protegee>
          }
        />
        <Route
          path="/demandes"
          element={
            <Protegee permissions={['demande.lire']}>
              <DemandesPage />
            </Protegee>
          }
        />
        <Route
          path="/adherents/:id"
          element={
            <Protegee permissions={['personne.lire']}>
              <AdherentDetailPage />
            </Protegee>
          }
        />
        {/* Anciens chemins : le modele « famille » a disparu, mais des
            liens ou des favoris peuvent encore y pointer. */}
        <Route path="/familles" element={<Navigate to="/adherents" replace />} />
        <Route path="/personnes" element={<Navigate to="/adherents" replace />} />

        <Route
          path="/inscriptions"
          element={
            <Protegee permissions={['inscription.lire']}>
              <InscriptionsPage />
            </Protegee>
          }
        />
        <Route
          path="/planning"
          element={
            <Protegee permissions={['creneau.lire']}>
              <PlanningPage />
            </Protegee>
          }
        />
        <Route
          path="/creneaux"
          element={
            <Protegee permissions={['creneau.lire']}>
              <CreneauxPage />
            </Protegee>
          }
        />
        <Route
          path="/presences"
          element={
            <Protegee permissions={['presence.lire']}>
              <ListePresencePage />
            </Protegee>
          }
        />
        <Route
          path="/creneaux/:id/presences"
          element={
            <Protegee permissions={['presence.lire']}>
              <PresencesPage />
            </Protegee>
          }
        />

        <Route path="/paiements" element={<PaiementsPage />} />
        <Route
          path="/calendrier"
          element={
            <Protegee permissions={['calendrier.lire']}>
              <CalendrierPage />
            </Protegee>
          }
        />

        <Route
          path="/activites"
          element={
            <Protegee permissions={['activite.lire']}>
              <ActivitesPage />
            </Protegee>
          }
        />
        <Route
          path="/types-activite"
          element={
            <Protegee permissions={['type_activite.lire']}>
              <TypesActivitePage />
            </Protegee>
          }
        />
        <Route
          path="/etablissements"
          element={
            <Protegee permissions={['etablissement.gerer']}>
              <EtablissementsPage />
            </Protegee>
          }
        />
        <Route
          path="/types-reduction"
          element={
            <Protegee permissions={['type_reduction.lire']}>
              <TypesReductionPage />
            </Protegee>
          }
        />
        <Route
          path="/lieux"
          element={
            <Protegee permissions={['lieu.lire']}>
              <LieuxPage />
            </Protegee>
          }
        />
        <Route
          path="/personnel"
          element={
            <Protegee permissions={['personnel.lire']}>
              <PersonnelPage />
            </Protegee>
          }
        />
        <Route
          path="/personnel/:id"
          element={
            <Protegee permissions={['personnel.lire']}>
              <PersonnelDetailPage />
            </Protegee>
          }
        />
        {/* Ancien chemin : la table et l'ecran s'appellent desormais
            Personnel, mais des favoris peuvent encore pointer ici. */}
        <Route path="/salaries" element={<Navigate to="/personnel" replace />} />
        <Route
          path="/contrats"
          element={
            <Protegee permissions={['contrat.lire']}>
              <ContratsPage />
            </Protegee>
          }
        />
        <Route
          path="/paie"
          element={
            <Protegee permissions={['contrat.lire']}>
              <PaiePage />
            </Protegee>
          }
        />
        <Route
          path="/annees"
          element={
            <Protegee permissions={['annee.lire']}>
              <AnneesPage />
            </Protegee>
          }
        />
        {/* Les nouveautes : pour tout utilisateur connecte, le contenu se
            filtrant lui-meme par permission. */}
        <Route path="/nouveautes" element={<NouveautesPage />} />
        <Route
          path="/annees/mode-operatoire"
          element={
            <Protegee permissions={['annee.lire']}>
              <ModeOperatoireAnneePage />
            </Protegee>
          }
        />

        <Route
          path="/admin/entites"
          element={
            <Protegee permissions={['entite.creer', 'entite.modifier']}>
              <EntitesPage />
            </Protegee>
          }
        />
        <Route
          path="/admin/utilisateurs"
          element={
            <Protegee permissions={['utilisateur.lire']}>
              <UtilisateursPage />
            </Protegee>
          }
        />
        <Route
          path="/admin/roles"
          element={
            <Protegee permissions={['role.lire']}>
              <RolesPage />
            </Protegee>
          }
        />
        <Route
          path="/admin/parametres"
          element={
            <Protegee permissions={['parametre_systeme.gerer']}>
              <ParametresPage />
            </Protegee>
          }
        />
        <Route
          path="/admin/audit"
          element={
            <Protegee permissions={['audit.lire']}>
              <AuditPage />
            </Protegee>
          }
        />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <FournisseurAuth>
        <Routage />
      </FournisseurAuth>
    </BrowserRouter>
  );
}
